import { forwardRef, type ForwardedRef, type HTMLAttributes } from 'react';
import { cn } from '@virginexperiencedays/tailwind';

import {
  DisplayHeading,
  type DisplayHeadingProps,
} from '@virginexperiencedays/components-v2/src/typography/DisplayHeading';
import { DyPromoService, vedPromosConfig } from '@virginexperiencedays/promos';
import { useWindowWidth } from '@virginexperiencedays/hooks';

import { ProductCardRenderer } from '../shared/ProductCardRenderer';
import { trackProductClick } from './tracking';
import { useRecommendations } from '../../../libs/dy/useRecommendations';
import { useIsProductCardsV2 } from '../search/ProductCard';

import type { Product } from '@virginexperiencedays/dy';

type Props = {
  /**
   * The number of items to offset the position tracking by.
   * This is to allow for additional items to be rendered before the grid.
   */
  itemsOffset?: number;
} & HTMLAttributes<HTMLDivElement>;

export const ProductGrid = forwardRef(function ProductGrid(
  { itemsOffset = 0, ...props }: Props,
  ref: ForwardedRef<HTMLDivElement>
) {
  const isProductCardsV2 = useIsProductCardsV2();
  const { width } = useWindowWidth();
  const { recommendations: products, isLoading, isValidating } = useRecommendations();

  const handleTracking = (item: Product, index: number): void => {
    trackProductClick({ product: item, position: index + itemsOffset });
  };

  const gridStyle = isProductCardsV2 && styles.grid.handpicked.v2;
  // bump up to 5 columns only for v2 desktop (default to 4 for initial render)
  const numColumns = isProductCardsV2 && width >= 1200 ? 5 : 4;

  /*
   * The reason to slice them and render only if there's more than numColumns is
   * because we render that many for products in the product column section, and
   * this grid is effectively continuation of the results.
   */
  if (products?.length > numColumns) {
    const promoService = new DyPromoService({
      activePromos: process.env.NEXT_PUBLIC_ACTIVE_PROMOS as string,
      promosConfiguration: vedPromosConfig,
    });

    return (
      <Container ref={ref} {...props}>
        <StyledHeading>Handpicked experiences</StyledHeading>
        <StyledList className={gridStyle}>
          {products?.slice(numColumns, products.length)?.map((item, index) => {
            const productPromo = promoService.getProductPromo(item);

            return (
              <li key={item.slug}>
                <ProductCardRenderer
                  index={index}
                  render="card"
                  item={{ ...item, productPromo }}
                  onClick={() => handleTracking(item, index)}
                />
              </li>
            );
          })}
        </StyledList>
      </Container>
    );
  }

  if (!isLoading && !isValidating && !!products) {
    return null;
  }

  // Initial render with skeletons
  return (
    <Container ref={ref} {...props}>
      <StyledHeading>Handpicked experiences</StyledHeading>
      <StyledList className={gridStyle}>
        {Array.from({
          length: isProductCardsV2 ? 12 : 15,
        })?.map((_, index) => (
          <li key={index}>
            <ProductCardRenderer render="skeleton" index={index} />
          </li>
        ))}
      </StyledList>
    </Container>
  );
});

export const Container = forwardRef(function Container(
  { children, className, ...rest }: HTMLAttributes<HTMLDivElement>,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <div className={cn('my-10', className)} ref={ref} {...rest}>
      {children}
    </div>
  );
});

export const StyledHeading = ({ children, className, ...props }: DisplayHeadingProps) => (
  <DisplayHeading className={cn('mb-4', className)} size="2" {...props}>
    {children}
  </DisplayHeading>
);

export const StyledList = ({ children, className, ...props }: HTMLAttributes<HTMLUListElement>) => (
  <ul
    className={cn('list-style-none m-0 grid p-0 text-left ', styles.grid.default, className)}
    {...props}
  >
    {children}
  </ul>
);

export const styles = {
  grid: {
    default: 'grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
    handpicked: {
      v2: 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
    },
    results: {
      v1: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4',
      v2: 'grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5',
    },
  },
  productCard: {
    title: {
      v2: 'line-clamp-3',
    },
  },
};
